/**
 * Pure derivation for backfill progress — extracted from `useBackfillJob` so the rules can be tested
 * without a QueryClient or auth provider (STANDARDS: when testing a component is painful, test the
 * logic).
 */
import type { BackfillJob, BackfillJobStatus } from "../utils/apiConfig";

/** Statuses the worker never advances from on its own. */
const TERMINAL: readonly BackfillJobStatus[] = ["COMPLETE", "FAILED", "INTERRUPTED"];

export interface BackfillProgress {
  readonly job: BackfillJob | null;
  /** The worker is alive and advancing — drives the banner + in-page notices. */
  readonly isRunning: boolean;
  readonly isComplete: boolean;
  /** Explicitly failed or interrupted. */
  readonly isFailed: boolean;
  /** 0-100, or null before `gamesTotal` is known (the FETCHING phase reports 0). */
  readonly percent: number | null;
  /** Whether to keep polling. False for terminal jobs — the backend marks stale jobs INTERRUPTED,
   *  so the frontend only needs to watch for terminal statuses. */
  readonly shouldPoll: boolean;
}

export function isTerminalStatus(status: BackfillJobStatus | undefined): boolean {
  return !!status && TERMINAL.includes(status);
}

export function deriveProgress(job: BackfillJob | null | undefined, now: number): BackfillProgress {
  const terminal = isTerminalStatus(job?.status);
  const total = job?.gamesTotal ?? 0;
  return {
    job: job ?? null,
    isRunning: !!job && !terminal,
    isComplete: job?.status === "COMPLETE",
    isFailed: !!job && (job.status === "FAILED" || job.status === "INTERRUPTED"),
    percent: total > 0 ? Math.min(100, Math.round(((job?.gamesComplete ?? 0) / total) * 100)) : null,
    shouldPoll: !!job && !terminal,
  };
}
